"use client";

import { useActionState } from "react";
import { ArrowUpRight, Mail } from "lucide-react";
import {
  subscribeNewsletter,
  type NewsletterState,
} from "@/lib/actions/newsletter";
import { cn } from "@/lib/format";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Fields";

type Props = {
  variant?: "default" | "pill" | "panel" | "stacked";
  placeholder?: string;
  submitLabel?: string;
};

export function NewsletterForm({
  variant = "default",
  placeholder = "you@example.com",
  submitLabel = "Subscribe",
}: Props) {
  const [state, action, pending] = useActionState<
    NewsletterState | null,
    FormData
  >(subscribeNewsletter, null);

  if (variant === "stacked") {
    return (
      <form action={action} className="space-y-3 mt-6">
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden
          className="absolute left-[-9999px]"
        />
        <label className="sr-only" htmlFor="newsletter-name-stacked">
          Full Name
        </label>
        <Input
          id="newsletter-name-stacked"
          name="name"
          type="text"
          placeholder="Full Name*"
          className="form-control flex-[0_0_50%] h-[44px] border border-headings/40 bg-transparent   text-headings rounded placeholder:text-headings/80 text-xs md:text-sm"
        />
        <label className="sr-only" htmlFor="newsletter-email-stacked">
          Email
        </label>
        <Input
          id="newsletter-email-stacked"
          name="email"
          type="email"
          required
          placeholder={placeholder}
          className="form-control flex-[0_0_50%] h-[44px] border border-headings/40 bg-transparent   text-headings rounded placeholder:text-headings/80 text-xs md:text-sm"
        />
        <Button
          type="submit"
          variant="primary"
          disabled={pending}
          className="min-h-[44px] uppercase"
        >
          {pending ? "Subscribing…" : submitLabel}
        </Button>
        {state ? (
          <p
            className={cn(
              "text-xs",
              state.ok ? "text-secondary-dark" : "text-red-600",
            )}
            role="status"
          >
            {state.message}
          </p>
        ) : null}
      </form>
    );
  }

  if (variant === "panel") {
    return (
      <form
        action={action}
        className="overflow-hidden rounded-[4px] border border-paper/10"
      >
        <div className="flex items-center justify-between gap-4 border-b border-paper/15 px-5 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="inline-grid size-6 shrink-0 place-items-center rounded-full bg-white/5 text-primary">
              <Mail size={12} strokeWidth={1.8} aria-hidden />
            </span>
            <span className="truncate text-[11px] font-medium uppercase leading-none tracking-[0.15em] text-paper/80">
              Sign up for our newsletter
            </span>
          </div>
        </div>
        <div className="space-y-5 p-5">
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden
            className="absolute left-[-9999px]"
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="block text-[10px] font-semibold uppercase leading-none tracking-[0.15em] text-paper/85">
                Name
              </span>
              <input
                name="name"
                type="text"
                placeholder="Full Name"
                className="mt-2 w-full border-0 border-b border-paper/20 bg-transparent px-0 py-2.5 text-[15px] text-paper outline-none transition-colors placeholder:text-paper/55 focus:border-primary"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] font-semibold uppercase leading-none tracking-[0.15em] text-paper/85">
                Email
              </span>
              <input
                name="email"
                type="email"
                required
                placeholder={placeholder}
                className="mt-2 w-full border-0 border-b border-paper/20 bg-transparent px-0 py-2.5 text-[15px] text-paper outline-none transition-colors placeholder:text-paper/55 focus:border-primary"
              />
            </label>
          </div>
          <div className="flex">
            {/* <p className="max-w-[28ch] text-[10px] font-medium uppercase leading-[1.5] tracking-[0.2em] text-paper/75">
              No spam. You agree to our terms and conditions.
            </p> */}
            <button
              type="submit"
              disabled={pending}
              className="inline-flex h-[33px] items-center px-3 rounded-[6px] bg-paper/90 py-1  text-[13px] font-semibold uppercase leading-none tracking-[0.075em] text-[#16202B] transition-colors hover:bg-white disabled:opacity-50"
            >
              {pending ? "Sending" : submitLabel}
              <span className="ml-2 rounded-full inline-grid size-5 place-items-center bg-primary text-white">
                <ArrowUpRight size={12} strokeWidth={1.9} aria-hidden />
              </span>
            </button>
          </div>
          {state ? (
            <p
              className={cn(
                "text-xs",
                state.ok ? "text-primary" : "text-red-300",
              )}
              role="status"
            >
              {state.message}
            </p>
          ) : null}
        </div>
      </form>
    );
  }

  if (variant === "pill") {
    return (
      <form action={action} className="space-y-3">
        <label htmlFor="newsletter-email" className="sr-only">
          Email
        </label>
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden
          className="absolute left-[-9999px]"
        />
        <div className="flex w-full max-w-md rounded-full bg-white/10 border border-white/15 p-1 focus-within:bg-white/15 focus-within:border-white/30 transition-colors">
          <input
            id="newsletter-email"
            name="email"
            type="email"
            required
            placeholder={placeholder}
            aria-label="Email address"
            className="flex-1 min-w-0 bg-transparent text-white border-none text-sm outline-none px-4 placeholder:text-white/50 focus:ring-0"
          />
          <button
            type="submit"
            disabled={pending}
            className="whitespace-nowrap rounded-full bg-white text-primary px-5 py-2 text-sm font-medium hover:bg-secondary transition-colors disabled:opacity-50"
          >
            {pending ? "Subscribing…" : submitLabel}
          </button>
        </div>
        {state ? (
          <p
            className={cn(
              "text-xs",
              state.ok ? "text-secondary" : "text-red-600",
            )}
            role="status"
          >
            {state.message}
          </p>
        ) : null}
      </form>
    );
  }

  return (
    <form action={action} className="space-y-3">
      <label htmlFor="newsletter-email" className="sr-only">
        Email
      </label>
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden
        className="absolute left-[-9999px]"
      />
      <div className="flex gap-2">
        <Input
          id="newsletter-email"
          name="email"
          type="email"
          required
          placeholder={placeholder}
        />
        <Button type="submit" variant="primary" disabled={pending}>
          {pending ? "Subscribing…" : submitLabel}
        </Button>
      </div>
      {state ? (
        <p
          className={cn(
            "text-xs",
            state.ok ? "text-secondary-dark" : "text-red-600",
          )}
          role="status"
        >
          {state.message}
        </p>
      ) : null}
    </form>
  );
}
